/**
 * Drafts data hooks — PEL-823.
 *
 * Thin TanStack Query wrappers over `@pelipost/consumer-api-client` drafts
 * module. Used by Home's "Drafts" card and the /app/drafts list page.
 *
 * Cache keys:
 *   ['drafts']                       — full list
 *   ['drafts', id]                   — single draft
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  Draft,
  CreateDraftRequest,
  UpdateDraftRequest,
} from '@pelipost/consumer-api-client';
import { api } from '../lib/api/client';

export const draftsKeys = {
  all: ['drafts'] as const,
  detail: (id: string) => ['drafts', id] as const,
};

/** List all drafts for the signed-in user. */
export function useDraftsList(
  options?: Omit<UseQueryOptions<Draft[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Draft[]>({
    queryKey: draftsKeys.all,
    queryFn: () => api.drafts.listDrafts(),
    ...options,
  });
}

/** Fetch a single draft. Disabled when id is empty. */
export function useDraft(id: string | undefined) {
  return useQuery<Draft>({
    queryKey: id ? draftsKeys.detail(id) : ['drafts', 'disabled'],
    queryFn: () => api.drafts.getDraft(id as string),
    enabled: Boolean(id),
  });
}

/** Create a new draft. Invalidates the list. */
export function useCreateDraft() {
  const qc = useQueryClient();
  return useMutation<Draft, Error, CreateDraftRequest>({
    mutationFn: (data) => api.drafts.createDraft(data),
    onSuccess: (draft) => {
      qc.invalidateQueries({ queryKey: draftsKeys.all });
      qc.setQueryData(draftsKeys.detail(draft.id), draft);
    },
  });
}

/** Update an existing draft. */
export function useUpdateDraft(id: string) {
  const qc = useQueryClient();
  return useMutation<Draft, Error, UpdateDraftRequest>({
    mutationFn: (data) => api.drafts.updateDraft(id, data),
    onSuccess: (draft) => {
      qc.setQueryData(draftsKeys.detail(id), draft);
      qc.invalidateQueries({ queryKey: draftsKeys.all });
    },
  });
}

/** Delete a draft. Drops it from the cache on success. */
export function useDeleteDraft() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.drafts.deleteDraft(id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: draftsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: draftsKeys.all });
    },
  });
}
